import { useRef, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * O formulário que nasce do que está gravado, e não se perde quando a tela se refaz.
 *
 * As seções do RDO editam listas inteiras — três turnos, as tarefas avulsas, o efetivo
 * por função, o equipamento — e cada uma tem o seu botão de gravar. Entre digitar e
 * gravar existe um rascunho local, e é ele que este gancho guarda.
 *
 * ## O problema que ele resolve
 *
 * `useLive` refaz **todas** as consultas da tela a cada escrita, em qualquer seção
 * (`use-command.ts`). Um `useEffect` que copiasse o valor do servidor para o rascunho a
 * cada resposta apagaria o que a pessoa estava digitando no clima porque alguém gravou o
 * equipamento — e apagaria sem aviso, que é a pior forma.
 *
 * Então o rascunho só é refeito quando **o valor gravado daquela seção** muda: trocar de
 * dia, gravar aquela seção, ou outra pessoa ter gravado. A comparação é pelo conteúdo, e
 * não pela identidade do objeto, porque a resposta do servidor é sempre um objeto novo
 * ainda que os dados sejam idênticos.
 *
 * ## Por que a atualização acontece no render
 *
 * É o padrão de "ajustar estado quando a prop muda": o React descarta a árvore e
 * re-renderiza antes de pintar, sem o quadro intermediário que o `useEffect` mostraria.
 * Com o efeito, quem gravasse o efetivo veria o número antigo por um instante.
 */
export function useDraft<S, D>(
  gravado: S,
  montar: (gravado: S) => D,
): [D, Dispatch<SetStateAction<D>>] {
  const assinatura = JSON.stringify(gravado ?? null);
  const [rascunho, setRascunho] = useState<D>(() => montar(gravado));
  const anterior = useRef(assinatura);

  if (anterior.current !== assinatura) {
    anterior.current = assinatura;
    setRascunho(montar(gravado));
  }

  return [rascunho, setRascunho];
}
